import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Chip, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import NumberSelector from '../common/ui/NumberSelector';
import { MultipleTicketsUIObjectData, TicketData } from '../api';
import {
  activePriceOptions,
  activeTickets,
  findMultipleTicketsPrice,
  MULTIPLE_TICKETS_DETAILS_FIELD_NAME,
  MultipleTicketsQuantities,
  sumCountingTickets,
  ticketCountsTowardCapacity,
} from '../common/multipleTicketsUtils';
import { useLanguage } from '../common/GeneralUtils';
import { customerFacingPriceFormatter } from '../utils/utils';
import { OrderPageState } from './types/OrderPageState';

type MultipleTicketsUiObjectProps = {
  uiObject: MultipleTicketsUIObjectData;
  orderState: OrderPageState;
  setMultipleTicketsDetails: (values: MultipleTicketsQuantities) => void;
  isLoadingDates: boolean;
};

export const MultipleTicketsUiObject = ({
  uiObject,
  orderState,
  setMultipleTicketsDetails,
  isLoadingDates,
}: MultipleTicketsUiObjectProps) => {
  const language = useLanguage();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const active = useMemo(() => activeTickets(uiObject), [uiObject]);
  const activeIds = useMemo(() => new Set(active.map((t) => t.id)), [active]);

  const readStoredQuantities = (): MultipleTicketsQuantities => {
    const stored = orderState.details.custom_details[MULTIPLE_TICKETS_DETAILS_FIELD_NAME] as
      | MultipleTicketsQuantities
      | undefined;
    return stored && typeof stored === 'object' ? stored : {};
  };

  const getInitialValues = (tickets: TicketData[]): MultipleTicketsQuantities => {
    const stored = readStoredQuantities();
    const hasStored = Object.keys(stored).length > 0;
    return tickets.reduce((acc, ticket) => {
      if (hasStored) {
        acc[ticket.id] = stored[ticket.id] ?? 0;
      } else if (ticket.default_ticket_group_size) {
        acc[ticket.id] = ticket.default_ticket_group_size;
      } else {
        acc[ticket.id] = 0;
      }
      return acc;
    }, {} as MultipleTicketsQuantities);
  };

  const debouncedSetMultipleTicketsDetails = useCallback(
    debounce((newValues: MultipleTicketsQuantities) => {
      setMultipleTicketsDetails(newValues);
    }, 300),
    [setMultipleTicketsDetails],
  );

  const [ticketValues, setTicketValues] = useState<MultipleTicketsQuantities>(getInitialValues(active));

  const mergeWithArchived = (activeValues: MultipleTicketsQuantities): MultipleTicketsQuantities => {
    const stored = readStoredQuantities();
    const archivedPart: MultipleTicketsQuantities = {};
    for (const [id, qty] of Object.entries(stored)) {
      if (!activeIds.has(id) && qty > 0) archivedPart[id] = qty;
    }
    return { ...archivedPart, ...activeValues };
  };

  useEffect(() => {
    setTicketValues(getInitialValues(active));
  }, [orderState.orderPage]);

  useEffect(() => {
    const hasDefaults = active.some((ticket) => ticket.default_ticket_group_size !== undefined);
    const stored = readStoredQuantities();
    if (hasDefaults && Object.keys(stored).length === 0) {
      const defaultValues = getInitialValues(active);
      debouncedSetMultipleTicketsDetails(mergeWithArchived(defaultValues));
    }
  }, []);

  const handleValueChange = (id: string, value: number) => {
    const newValues = { ...ticketValues, [id]: value };
    setTicketValues(newValues);
    debouncedSetMultipleTicketsDetails(mergeWithArchived(newValues));
  };

  const priceOptions = activePriceOptions(findMultipleTicketsPrice(orderState.orderPage));
  const priceByTicketId = useMemo(
    () => new Map(priceOptions.map((opt) => [opt.ticket_id, opt.price_per_unit])),
    [priceOptions],
  );

  const countingTotal = sumCountingTickets(ticketValues, uiObject);
  const isAtMaxCounting = !!uiObject.max_total_participants && countingTotal >= uiObject.max_total_participants;
  const shouldShowMinAlert = countingTotal < uiObject.min_total_participants && !!uiObject.min_total_alert?.[language];
  const shouldShowMaxAlert = isAtMaxCounting && !!uiObject.max_total_alert?.[language];

  return (
    <Grid container flexDirection="column" mb={3} gap={1}>
      <Typography>{uiObject.label[language]}</Typography>
      {shouldShowMinAlert && (
        <Alert severity={'warning'} variant={'outlined'} sx={{ maxWidth: '450px' }}>
          <Grid item>
            <Typography color="inherit">
              {uiObject.min_total_alert?.[language]?.replace('{{min}}', uiObject.min_total_participants.toString())}
            </Typography>
          </Grid>
        </Alert>
      )}
      <Grid container flexDirection={'column'} gap={3} xs={isSmallScreen ? 12 : 8}>
        {active.map((ticket) => {
          const currentValue = ticketValues[ticket.id] || 0;
          const pricePerUnit = priceByTicketId.get(ticket.id) ?? 0;
          const counts = ticketCountsTowardCapacity(ticket);
          return (
            <Grid container justifyContent="space-between" key={ticket.id} alignItems="center">
              <Grid gap={1}>
                <Grid container alignItems="center" gap={1}>
                  <Typography variant={'h6'}>{ticket.label[language]}</Typography>
                  {!counts && (
                    <Chip
                      label={t('multipleTicketsUiObject.addOnBadge')}
                      size="small"
                      variant="outlined"
                      color="default"
                    />
                  )}
                </Grid>
                <Typography color="text.secondary">
                  {ticket.default_ticket_group_size
                    ? customerFacingPriceFormatter(pricePerUnit * ticket.default_ticket_group_size, orderState.account)
                    : t('multipleTicketsUiObject.perperson', {
                        price: customerFacingPriceFormatter(pricePerUnit, orderState.account),
                      })}
                </Typography>
              </Grid>
              {!ticket.default_ticket_group_size && (
                <NumberSelector
                  isRemoveDisabled={currentValue === 0}
                  isAddButtonDisabled={counts && isAtMaxCounting}
                  value={currentValue}
                  setValue={(val: number) => handleValueChange(ticket.id, val)}
                  disabled={isLoadingDates}
                  min={0}
                />
              )}
            </Grid>
          );
        })}
      </Grid>
      {isAtMaxCounting && shouldShowMaxAlert && (
        <Alert severity={'warning'} variant={'outlined'} sx={{ maxWidth: '350px' }}>
          <Grid item>
            <Typography color="inherit">
              {uiObject.max_total_alert?.[language]?.replace('{{max}}', uiObject.max_total_participants.toString())}
            </Typography>
          </Grid>
        </Alert>
      )}
    </Grid>
  );
};
